<template>
  <div>
    <div class="layout-column flex">

      <!-- Leistungserbringer -->

      <div class="flex-gt-sm-50 flex-100 px-2 mb-2" v-if="isMedicalCommunity && !isImport">
        <validation-provider tag="div"
                             :name="path + '.reporter'"
                             :ref="path + '.reporter'"
                             rules="reporter_is_required"
                             v-slot="{ errors }">
          <v-select
            :color="compColor.primeColor"
            :label="$t('COMPONENT_CANCER_MESSAGE_DETAILS.CARE_PROVIDER') + ' *'"
            v-model="model.reporter"
            :items="physicianList"
            outlined
            @change="triggerChanges(path + '.reporter')"
            :hide-details="!errors || errors.length <= 0"
            :disabled="isDisabled"
            :dense="isDense || denseFields"
            class="ma-0"
            :value-comparator="compareReporterInList"
            :error-messages="errors[0]"
            :menu-props="{ bottom: true, offsetY: true }"
            :dark="compColor.isInDarkMode">
            <template v-slot:selection='{ item }'>
              <span v-if="!compColor.isInDarkMode" :style="{ 'color': isDisabled ? 'rgba(0, 0, 0, 0.38)' : '#445a64', 'font-size': denseFields ? '14.4px !important' : '16px !important'}">
                {{ item.value.name }}</span>
              <span v-if="compColor.isInDarkMode" :style="{ 'color': isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'white', 'font-size': denseFields ? '14.4px !important' : '16px !important'}">
                {{ item.value.name }}</span>
            </template>

            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on"
                           :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                <v-list-item-content class="py-0 layout-row">
                  <div  class="py-0 layout-row">
                    <lx-text-highlight :text="item.value.name" :search-string="item.value.name"
                                       :color="compColor.prime"></lx-text-highlight>
                    <span v-if="item.value.lanr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(LANR: {{ item.value.lanr }} | Melder-ID: {{ item.value.melderId }})</span>
                    <span v-if="item.value.iknr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(IKNR: {{ item.value.iknr }} | Melder-ID: {{ item.value.melderId }})</span>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </template>

            <template v-slot:no-data>
              <div class="layout-row pt-1 pb-1 pl-3 pr-3">
                <div class="flex layout-row mr-3">
                  {{ $t('NO_ROWS') }}
                </div>
              </div>
            </template>
          </v-select>
        </validation-provider>
      </div>

      <div class="flex-gt-sm-100 flex-100 px-2 mb-1" v-if="reporterIsNotInList && !isImport">
        <lx-alert-box color="orange"
                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1">
          {{
            $t('ERROR_REPORTER_NOT_IN_LIST', {
              id: (model.reporter || {}).melderId,
              name: (model.reporter || {}).name,
              labelIdentNr: model.reporter && model.reporter.lanr ? 'LANR' : 'IKNR',
              identNr: (model.reporter || {}).lanr || (model.reporter || {}).iknr
            })
          }}
        </lx-alert-box>
      </div>

      <!-- Eigenleistung -->

      <div class="flex-gt-sm-50 flex-100 px-2 mt-2 layout-row">
        <label :style="{color: compColor.primeColor}" class="pt-2 mr-3 ml-1">
          {{ formKey==='death' ? $t('FORMS_DIVERSE.REPORTER_LABEL_PERSONAL_CONTRIBUTION_DEATH') : $t('FORMS_DIVERSE.REPORTER_LABEL_PERSONAL_CONTRIBUTION') }} *</label>
        <validation-provider tag="div"
                             class="layout-column"
                             :name="path + '.eigenleistung'"
                             :ref="path + '.eigenleistung'"
                             rules="required"
                             v-slot="{ errors }">
          <v-btn-toggle v-model="model.eigenleistung"
                        dense
                        class="ml-6"
                        :mandatory="!!model.eigenleistung"
                        style="margin-top:2px; background-color: transparent;"
                        :color="compColor.prime">
            <v-btn :key="index" v-for="(kind, index) in listOfPersonalContribution" :value="kind.value" :disabled="isDisabled">
              {{ kind.text }}
            </v-btn>
          </v-btn-toggle>
          <span class="mt-1" style="color:#ff5252; font-size:12px;">{{ errors[0] }}</span>
        </validation-provider>
      </div>

      <!-- Header Zusatzitem, nur, wenn Zusatzitems existieren -->

      <div class="flex-100 px-3 py-1 mb-4 mx-2 mt-4 layout-column" :class="isDarkMode ? 'blue-grey darken-3': compColor.prime + ' lighten-5'"
           style="border-radius: 4px;"
           v-if="showHeaderOfAdditionalItems">
        <span class="body-2 font-weight-bold" :style="{color: compColor.primeColor}">{{ $t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_HEADER') }}</span>
        <span class="body-2" style="font-style: italic" :style="{color: compColor.primeColor}">{{ $t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_SUB_LINE') }}</span>
      </div>

      <!-- Liste Vorgaben für Zusatzitems vom Krebsregister -->

      <div :key="index" v-for="(listItem, index) in additionalItemsList"
           class="layout-column pa-0 mb-n3">

        <!-- Liste Zusatzitem -->

        <lx-expansion-panels accordion multiple v-model="additionalItemsPanel">
          <lx-expansion-panel v-for="(item, itemIndex) in model.zusatzitems"
                              :key="itemIndex"
                              class="mx-2"
                              v-show="listItem.name === item.name"
                              :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                              style="border-radius:4px;box-shadow:none !important;"
                              :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }"
                              tooltip-text-open-icon="Details anzeigen und bearbeiten">
            <lx-expansion-panel-header-with-slot v-if="listItem.name === item.name">
              <div class="flex layout-row">
                <div class="flex layout-row px-3 pt-3">

                  <!-- Name Zusatzitem -->

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div"
                                         :name="path + '.zusatzitems.' + itemIndex + '.name'"
                                         :ref="path + '.zusatzitems.' + itemIndex + '.name'"
                                         :rules="{required: true}"
                                         v-slot="{ errors }">
                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        :color="compColor.primeColor"
                        :dense="denseFields"
                        :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_NAME')"
                        outlined
                        disabled
                        class="mb-3"
                        :hide-details="!errors || errors.length <= 0"
                        :error-messages="errors[0]"
                        v-model="item.name"
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <!-- Wert (Auswahlliste) Zusatzitem -->

                  <div class="flex-gt-sm-50 flex-100 px-2" v-if="listItem.content.element_type === 'select'">
                    <validation-provider tag="div"
                                         :name="path + '.zusatzitems.' + itemIndex + '.value'"
                                         :ref="path + '.zusatzitems.' + itemIndex + '.value'"
                                         rules="required"
                                         v-slot="{ errors }">
                      <v-select
                        :color="compColor.primeColor"
                        :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_VALUE') + ' *'"
                        v-model="item.value"
                        :items="listItem.content.select_values"
                        outlined
                        class="mb-3"
                        :disabled="isDisabled"
                        :hide-details="!errors || errors.length <= 0"
                        :dense="denseFields"
                        :error-messages="errors[0]"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :dark="compColor.isInDarkMode">
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-on="on"
                                       :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                            <v-list-item-content class="py-0">
                              <label style="cursor:pointer;"
                                     :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                     class="lighten-4 text--darken-2">{{ item.text }}</label>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-select>
                    </validation-provider>
                  </div>

                  <!-- Wert (Textfeld) Zusatzitem -->

                  <div class="flex-gt-sm-50 flex-100 px-2" v-if="listItem.content.element_type === 'input'">
                    <validation-provider tag="div"
                                         :name="path + '.zusatzitems.' + itemIndex + '.value'"
                                         :ref="path + '.zusatzitems.' + itemIndex + '.value'"
                                         rules="required|trim_string"
                                         v-slot="{ errors }">
                      <long-input
                        :color="compColor.primeColor"
                        :name="path + '.zusatzitems.' + itemIndex + '.value'"
                        :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_VALUE') + ' *'"
                        v-model="item.value"
                        class="mb-n1"
                        :disabled="isDisabled"
                        :dense="denseFields"
                        :error="errors[0]"
                      ></long-input>
                    </validation-provider>
                  </div>
                </div>
              </div>
            </lx-expansion-panel-header-with-slot>

            <lx-expansion-panel-content :style="{'background': compColor.lxExpansion.background}" :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}">
              <div class="layout-row layout-wrap flex px-3 pt-2">

                <!-- Datum Zusatzitem -->

                <div class="flex-gt-sm-33 flex-100 pl-2 pr-1">
                  <validation-provider tag="div"
                                       :name="path + '.zusatzitems.' + itemIndex + '.date.date'"
                                       :ref="path + '.zusatzitems.' + itemIndex + '.date.date'"
                                       :rules="{german_date:true}"
                                       v-slot="{ errors }">
                    <lx-datepicker
                      :color="compColor.textInput"
                      :name="path + '.zusatzitems.' + itemIndex + '.date.date'"
                      :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_DATE')"
                      class="mr-1"
                      @input="setDateAccuracyAfterDate(itemIndex)"
                      :disabled="isDisabled"
                      :dense="denseFields"
                      :error="errors[0]"
                      v-model="item.date.date"
                    ></lx-datepicker>
                  </validation-provider>
                </div>

                <!-- Datumsgenauigkeit Datum Zusatzitem -->

                <div class="flex-gt-sm-33 flex-100 px-2 layout-row">
                  <validation-provider tag="div"
                                       class="flex"
                                       :name="path + '.zusatzitems.' + itemIndex + '.date.accuracy'"
                                       :ref="path + '.zusatzitems.' + itemIndex + '.date.accuracy'"
                                       rules=""
                                       v-slot="{ errors }">
                    <v-select
                      :color="compColor.primeColor"
                      :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_DATE_ACCURACY')"
                      v-model="item.date.accuracy"
                      :items="DateAccuracyTypes"
                      :disabled="isDisabled || !item.date.date"
                      outlined
                      :hide-details="!errors || errors.length <= 0"
                      :dense="denseFields"
                      @change="triggerChanges(path + '.zusatzitems.' + itemIndex + '.date.accuracy')"
                      :error-messages="errors[0]"
                      :menu-props="{ bottom: true, offsetY: true }"
                      :dark="compColor.isInDarkMode">
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-on="on"
                                     :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                          <v-list-item-content class="py-0">
                            <label style="cursor:pointer;"
                                   :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                   class="lighten-4 text--darken-2">{{ item.text }}</label>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-select>
                  </validation-provider>

                  <v-tooltip bottom open-delay="1000">
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-3"
                           :class="{'mt-1': !denseFields}"
                           style="cursor: pointer;">
                        <v-icon :color="compColor.prime" medium>info</v-icon>
                      </div>
                    </template>
                    <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
                  </v-tooltip>
                </div>

                <!-- Kommentar Zusatzitem -->

                <div class="flex-gt-sm-33 flex-100 px-2">
                  <validation-provider tag="div"
                                       :name="path + '.zusatzitems.' + itemIndex + '.comment'"
                                       :ref="path + '.zusatzitems.' + itemIndex + '.comment'"
                                       rules="trim_string"
                                       v-slot="{ errors }">
                    <long-input
                      :color="compColor.primeColor"
                      :name="path + '.zusatzitems.' + itemIndex + '.comment'"
                      :label="$t('FORMS_DIVERSE.REPORTER_ADDITIONAL_ITEM_COMMENT')"
                      v-model="item.comment"
                      @input="triggerChanges(path + '.zusatzitems.' + itemIndex + '.comment')"
                      :disabled="isDisabled"
                      :dense="denseFields"
                      :error="errors[0]"
                    ></long-input>
                  </validation-provider>
                </div>
              </div>
            </lx-expansion-panel-content>
          </lx-expansion-panel>
        </lx-expansion-panels>
      </div>

      <!-- Zertifizierung -->

      <div class="flex-gt-sm-50 flex-100 px-2 mt-2 layout-row" v-if="formKey==='register' || formKey==='progress' && reportReason==='statusaenderung'">
        <div class="flex-gt-sm-25 flex-100 pr-1">
          <validation-provider tag="div"
                               :name="path + '.zertifizierung'"
                               :ref="path + '.zertifizierung'"
                               rules=""
                               v-slot="{ errors }">
            <v-select
              :color="compColor.primeColor"
              :label="$t('FORMS_DIVERSE.CERTIFICATION.TITLE')"
              v-model="model.zertifizierung"
              :items="CertificationTypes"
              :disabled="isDisabled"
              outlined
              clearable
              :hide-details="!errors || errors.length <= 0"
              :dense="denseFields"
              :menu-props="{ bottom: true, offsetY: true }"
              :dark="compColor.isInDarkMode"
              :error="errors[0]">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on"
                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                  <v-list-item-content class="py-0">
                    <label style="cursor:pointer;"
                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </validation-provider>
        </div>

        <v-tooltip bottom open-delay="1000">
          <template v-slot:activator="{ on }">
            <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-3"
                 :class="{'mt-1': !denseFields}"
                 style="cursor: pointer;">
              <v-icon :color="compColor.prime" medium>info</v-icon>
            </div>
          </template>
          <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.CERTIFICATION.TOOLTIP') }}</p>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import LxTextHighlight from '../../lx_text_highlighter/LxTextHighlight'
import metaData from '../shared/formsMetaData'

export default {
  name: "Melder",
  components: {
    LxTextHighlight
  },
  mixins: [metaData],
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    path: {
      type: String
    },
    isDisabled: {
      type: Boolean
    },
    isDense: {
      type: Boolean
    },
    isMedicalCommunity: {
      type: Boolean
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    additionalItemsList: {
      type: Array,
      default: () => ([])
    },
    isImport: {
      type: Boolean,
      default: false
    },
    formKey: {
      type: String
    },
    reportReason: {
      type: String
    }
  },
  data () {
    return {
      visible: false,
      model: {
        zusatzitems: []
      },
      listOfPersonalContribution: [],
      listOfPersonalContributionJN: [
        { value: 'J', text: 'Ja'},
        { value: 'N', text: 'Nein'}
      ],
      listOfPersonalContributionJNU: [
        { value: 'J', text: 'Ja'},
        { value: 'N', text: 'Nein'},
        { value: 'U', text: 'Unbekannt'}
      ],
      DateAccuracyTypes: null,
      additionalItemsPanel: [],
      CertificationTypes: []
    }
  },
  created () {
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
    this.CertificationTypes = this.formsMetaData.CertificationTypes
    this.additionalItemsPanel = []
  },
  computed: {
    physicianList () {
      const user = this.authStore.getters.getAuthUserData && this.authStore.getters.getAuthUserData.user ? this.authStore.getters.getAuthUserData.user : {}
      let physicianArray = []

      if (user && user.cancer_form_details && user.cancer_form_details.medicalCommunity && user.cancer_form_details.medicalCommunity.physicians) {
        user.cancer_form_details.medicalCommunity.physicians.forEach(physician => {
          physicianArray.push( {
            value: {
              name: physician.name,
              melderId: physician.melderId,
              lanr: physician.lanr,
              iknr: physician.iknr
            }
          })
        })
      }

      return physicianArray
    },
    reporterIsNotInList () {
      return this.model.reporter && this.model.reporter.melderId && !this.physicianList.some(item => (this.model.reporter.lanr && item.value.lanr === this.model.reporter.lanr) || (this.model.reporter.iknr && item.value.iknr === this.model.reporter.iknr))
    },
    showHeaderOfAdditionalItems () {
      return this.isImport ? this.model.zusatzitems && this.model.zusatzitems.length > 0 : this.additionalItemsList && this.additionalItemsList.length > 0 || this.formKey==='register'
    }
  },
  methods: {
    /**
     *  Compare selected reporter with reporter in list to show in field if lanr or iknr are identical
     *
     * @param arrayElement The element from list of physicians
     * @param selectedItem The selected reporter
     * @returns {boolean}
     */
    compareReporterInList (arrayElement, selectedItem) {
      if (arrayElement?.lanr && selectedItem?.lanr && arrayElement?.lanr === selectedItem?.lanr) return true
      else if (arrayElement?.iknr && selectedItem?.iknr && arrayElement?.iknr === selectedItem?.iknr) return true
      return false
    },

    /**
     * Set reporter or sub-values of additional items manually on dirty because of validator do not react on changes of an object-value
     */
    triggerChanges (field) {
      if (this.validationRef.fields[field]) {
        this.validationRef.fields[field].changed = true
      }
    },

    /**
     * Set date accuracy per default after date input
     *
     * @param index
     */
    setDateAccuracyAfterDate (index) {
      // set datum_genauigkeit if not exists after changing datum in datepicker
      if (this.model.zusatzitems[index] && this.model.zusatzitems[index].date && this.model.zusatzitems[index].date.date && !this.model.zusatzitems[index].date.accuracy) {
        this.$set(this.model.zusatzitems[index].date, 'accuracy', 'E')
      } else if (!this.model.zusatzitems[index].date.date) {
        // delete date accuracy, if date was deleted
        this.$set(this.model.zusatzitems[index].date, 'accuracy', undefined)
      }

      // set dirty-flag of date on true to ensure, that auto-save will be triggered
      if (this.validationRef.fields[this.path + '.zusatzitems.' + index + '.date.date']) {
        this.validationRef.fields[this.path + '.zusatzitems.' + index + '.date.date'].changed = true
      }
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
          this.listOfPersonalContribution = this.model.eigenleistung === 'U' ? this.listOfPersonalContributionJNU : this.listOfPersonalContributionJN
        }
      },
      immediate: true
    }
  }
}
</script>
